import React from 'react';

import { ArrowLeft, Eye, MessageCircleQuestion, RefreshCw } from 'lucide-react';

import { useIsoProfitAccess } from '../../hooks/useIsoProfitAccess';
import { useFeature } from '../../hooks/useFeature';
import { useTypedSelector } from '../../store';
import { CREDIT_SHARE_NEEDED_LABEL } from '../../utils/pricingAssumptions';
import {
	formatCurrency,
	formatRatePlanType,
	formatTimestamp,
} from '../../utils/utils';
import { Alert, Button } from '../common';
import CustomizeFeeStructureModal from '../CustomizeFeeStructureModal';
import { openConcierge } from '../conciergeEvents';
import ExportButton from '../ExportButton';

import RecalculationInfo from './RecalculationInfo';
import { ISO_PROFIT_CARD_ID } from './AnalysisSideCards';
import SummaryCard, { SUMCARD_TONES } from './SummaryCard';
import { BTN_BACK, BTN_PRIMARY, BTN_SECONDARY, PILL_PRIMARY } from './v9';
import ViewSwitcher from './ViewSwitcher';

import type { RevenueAnalysis } from '../../types';

interface FeeStructureData {
	visaMastercardDiscoverCredit: number;
	visaMastercardDiscoverDebit: number;
	americanExpress: number;
	pinDebit: number;
	creditTransactions: number;
	debitTransactions: number;
	pinDebitTransactions: number;
	assessmentFees: boolean;
	amexRate: boolean;
}

interface AnalysisHeaderProps {
	/** ROC-72: hide savings, export and quote controls for a file that is not a statement. */
	hideAnalysis?: boolean;
	/** ROC-105: why Customize Quote is locked (flagged figures not confirmed yet). */
	quoteLockedReason?: string;
	/** ROC-88: why export is blocked (credit share not entered yet). */
	exportLockedReason?: string;
	/** ROC-88: the surcharge is not priced until the credit share is entered. */
	creditShareNeeded?: boolean;
	document: {
		id: string;
		name: string;
		date: string;
		status: string;
	};
	onBack: () => void;
	onToggleDocument: () => void;
	isDocumentVisible: boolean;
	onboardingContext: boolean;
	analysisData?: {
		processingMonth: string;
		duesFeesAssessments: any[];
		interchange: any[];
		authorizationAndDiscount: any[];
		otherFees: any[];
		processorName: string;
		processorNamePlus: string;
		totals: {
			ic: number;
			icPlus: number;
		};
		revenue_analysis?: any;
	};
	monthlySavingsData?: {
		value: number;
		isNegative: boolean;
	};
	// Backend-calculated totals (statement-type-aware)
	calculatedTotals?: {
		ic: number;
		icPlus: number;
		savings: {
			monthly: number;
			yearly: number;
			percent_lower: number;
			is_negative: boolean;
		} | null;
		effectiveRates: {
			current: number;
			comparison: number;
		} | null;
		volume: number | null;
		calculationMethod: string | null;
		totalSource?: 'document' | 'calculated';
		documentTotal?: {
			amount: number;
			label: string;
			page_number?: number;
		};
	};
	analysisType?: string;
	actionType?: string; // Action type (upload, rerun, recalculation)
	logsData?: any; // For progress calculation
	onCustomizeFeeStructure?: (feeData: FeeStructureData) => void;
	onRefetchData?: () => void;
	isLoading?: boolean;
	isFetching?: boolean;
	customRates?: any; // Custom rates from processing_metadata
	onRerun?: () => void;
	isRerunning?: boolean;
	revenueAnalysis?: RevenueAnalysis; // ISO profit - reactive to line item edits
	viewMode?: 'default' | 'user_edits' | 'customized_quote';
	onViewModeChange?: (
		view: 'default' | 'user_edits' | 'customized_quote',
	) => void;
	hasUserEdits?: boolean;
	hasCustomizedQuote?: boolean;
	// Statement-type mismatch banner (ROC-62): present only when the
	// classifier-detected type differs from the user-selected one
	typeMismatch?: {
		detectedRatePlanType: string;
		selectedRatePlanType: string;
		onRecalculateAsDetected: () => void;
		onDismiss: () => void;
		isRecalculating?: boolean;
	};
	/** Customize Quote modal; the parent owns it so the discount-rate dock can open it too. */
	isCustomizeModalOpen: boolean;
	onCustomizeModalOpenChange: (open: boolean) => void;
}

// StepNameEnum.RECALCULATION on the API.
const RECALCULATION_STEP = 'Recalculation';

const AnalysisHeader: React.FC<AnalysisHeaderProps> = ({
	quoteLockedReason,
	exportLockedReason,
	creditShareNeeded = false,
	document,
	onBack,
	onToggleDocument,
	isDocumentVisible,
	onboardingContext,
	analysisData,
	monthlySavingsData,
	calculatedTotals,
	analysisType,
	actionType,
	logsData,
	onCustomizeFeeStructure,
	onRefetchData,
	isLoading,
	isFetching,
	customRates,
	onRerun,
	isRerunning,
	revenueAnalysis,
	viewMode = 'default',
	onViewModeChange,
	hasUserEdits = false,
	hasCustomizedQuote = false,
	typeMismatch,
	hideAnalysis = false,
	isCustomizeModalOpen,
	onCustomizeModalOpenChange,
}) => {
	const showAnalysis = document.status === 'completed' && !hideAnalysis;
	const { canSeeIsoProfit } = useIsoProfitAccess();

	// Get tenant from Redux state for dynamic branding
	const tenant = useTypedSelector((state) => state.tenant.tenant);

	// Determine the company name to display
	// Priority: tenant name > "RocketFee" (default for main domains)
	const companyName = tenant?.name || 'RocketFee';

	const handleConfirmFeeStructure = (feeData: FeeStructureData) => {
		if (onCustomizeFeeStructure) {
			onCustomizeFeeStructure(feeData);
		}

		// Refetch data after customization
		if (onRefetchData) {
			onRefetchData();
		}

		onCustomizeModalOpenChange(false);
	};

	// The Concierge (ROC-92) only exists for tenants with the feature.
	const hasConcierge = useFeature('concierge');
	const askAboutThis = () =>
		openConcierge('analysis', {
			title: document.name,
			statement_id: document.id,
		});

	// Prefer backend-calculated effective rates; fall back to fees over volume.
	const effectiveRate = (side: 'ic' | 'icPlus'): number => {
		if (calculatedTotals?.effectiveRates) {
			return side === 'ic'
				? calculatedTotals.effectiveRates.current
				: calculatedTotals.effectiveRates.comparison;
		}
		if (!analysisData) return 0;
		const volumeOf = (value: unknown) =>
			parseFloat(value?.toString().replace(/[$,]/g, '') || '0');
		const totalVolume = [
			...analysisData.duesFeesAssessments,
			...analysisData.interchange,
			...analysisData.authorizationAndDiscount,
		].reduce(
			(sum, item) =>
				sum +
				volumeOf(item.ic?.volume) +
				(item.children || []).reduce(
					(childSum: number, child: { ic?: { volume?: unknown } }) =>
						childSum + volumeOf(child.ic?.volume),
					0,
				),
			0,
		);
		return totalVolume > 0
			? (analysisData.totals[side] / totalVolume) * 100
			: 0;
	};

	const cardLoading = !analysisData || Boolean(isLoading || isFetching);
	const savingsNegative = Boolean(monthlySavingsData?.isNegative);
	const savingsPercent = calculatedTotals?.savings
		? Math.abs(calculatedTotals.savings.percent_lower)
		: analysisData && monthlySavingsData && analysisData.totals.ic > 0
			? Math.abs((monthlySavingsData.value / analysisData.totals.ic) * 100)
			: 0;
	const bpsLower = analysisData
		? Math.round((effectiveRate('ic') - effectiveRate('icPlus')) * 100)
		: 0;
	// ROC-102: roles that may not see ISO profit get a note instead of the card.
	const iso = canSeeIsoProfit
		? revenueAnalysis || analysisData?.revenue_analysis
		: undefined;
	// Newest analysis log (the API sorts newest first); dock saves log as a recalculation.
	const analyzedAt = logsData?.logs?.find(
		(log: { step_name?: string }) => log.step_name !== RECALCULATION_STEP,
	)?.created_at;
	const recalculated = actionType === 'recalculation' || actionType === 'rerun';

	return (
		<div className="w-full bg-gray-50">
			{/* .subbar */}
			<div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4">
				<div className="flex min-w-0 items-center gap-4">
					{!onboardingContext && (
						<button type="button" onClick={onBack} className={BTN_BACK}>
							<ArrowLeft />
							Back
						</button>
					)}
					<div className="min-w-0">
						<div className="truncate text-lg font-semibold text-gray-900">
							{document.name}
							{analysisData?.processingMonth &&
								` · ${analysisData.processingMonth}`}
						</div>
						<div className="mt-2 flex flex-wrap items-center gap-2">
							<RecalculationInfo
								logsData={logsData}
								customRates={customRates}
								analysisType={analysisType}
								actionType={actionType}
							/>
							{showAnalysis && analyzedAt && !recalculated && (
								<span className={PILL_PRIMARY}>
									<RefreshCw />
									Analyzed {formatTimestamp(analyzedAt)}
								</span>
							)}
						</div>
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-3">
					{showAnalysis && !canSeeIsoProfit && (
						<span
							className="text-sm text-gray-500"
							title="An admin can change who sees profit under Team"
						>
							ISO profit is restricted for your role
						</span>
					)}
					{showAnalysis && onViewModeChange && (
						<ViewSwitcher
							currentView={viewMode}
							onViewChange={onViewModeChange}
							hasUserEdits={hasUserEdits || false}
							hasCustomizedQuote={hasCustomizedQuote || false}
						/>
					)}
					{hasConcierge && (
						<button
							type="button"
							onClick={askAboutThis}
							className={BTN_SECONDARY}
						>
							<MessageCircleQuestion />
							Ask about this
						</button>
					)}
					{onRerun && (
						<button
							type="button"
							onClick={onRerun}
							disabled={isRerunning}
							className={BTN_SECONDARY}
						>
							<RefreshCw className={isRerunning ? 'animate-spin' : ''} />
							Rerun Analysis
						</button>
					)}
					<button
						type="button"
						onClick={onToggleDocument}
						className={BTN_SECONDARY}
					>
						<Eye />
						View statement
					</button>
					{analysisData && showAnalysis && (
						<ExportButton
							analysisData={analysisData}
							fileId={document.id}
							viewMode={viewMode}
							lockedReason={exportLockedReason}
						/>
					)}
					{showAnalysis && (
						<button
							type="button"
							onClick={() => onCustomizeModalOpenChange(true)}
							className={BTN_PRIMARY}
							disabled={Boolean(quoteLockedReason)}
							title={quoteLockedReason}
						>
							Customize Quote
						</button>
					)}
				</div>
			</div>

			{/* .summary: compact cards, rate on the left and monthly fees on the right */}
			{showAnalysis && (
				<div
					className={`grid gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4 ${
						iso ? 'grid-cols-4' : 'grid-cols-3'
					}`}
				>
					<SummaryCard
						tone={SUMCARD_TONES.current}
						label="Current processor"
						v1={`${effectiveRate('ic').toFixed(2)}%`}
						l1="Rate"
						v2={formatCurrency(
							calculatedTotals?.ic ?? analysisData?.totals.ic ?? 0,
						)}
						l2="Monthly fees"
						loading={cardLoading}
					/>
					<SummaryCard
						tone={SUMCARD_TONES.proposed}
						label={`With ${companyName}`}
						v1={`${effectiveRate('icPlus').toFixed(2)}%`}
						l1={bpsLower > 0 ? `Rate · ${bpsLower} bps lower` : 'Rate'}
						v2={formatCurrency(
							calculatedTotals?.icPlus ?? analysisData?.totals.icPlus ?? 0,
						)}
						l2="Monthly fees"
						loading={cardLoading}
					/>
					<SummaryCard
						tone={savingsNegative ? SUMCARD_TONES.loss : SUMCARD_TONES.savings}
						label="Customer savings"
						v1={
							creditShareNeeded
								? CREDIT_SHARE_NEEDED_LABEL
								: formatCurrency(monthlySavingsData?.value ?? 0)
						}
						l1={`Monthly (${savingsPercent.toFixed(0)}% ${savingsNegative ? 'higher' : 'lower'})`}
						v2={
							creditShareNeeded
								? CREDIT_SHARE_NEEDED_LABEL
								: formatCurrency(
										calculatedTotals?.savings?.yearly ??
											(monthlySavingsData?.value ?? 0) * 12,
									)
						}
						l2="Yearly"
						loading={!monthlySavingsData || cardLoading}
					/>
					{iso && (
						<SummaryCard
							tone={SUMCARD_TONES.profit}
							label="ISO profit"
							v1={formatCurrency(iso.total_revenue)}
							l1="Monthly"
							v2={formatCurrency(iso.total_revenue * 12)}
							l2="Yearly"
							loading={cardLoading}
							onClick={() =>
								window.document
									.getElementById(ISO_PROFIT_CARD_ID)
									?.scrollIntoView({ behavior: 'smooth', block: 'center' })
							}
						/>
					)}
				</div>
			)}

			{showAnalysis && typeMismatch && (
				<div className="px-6 pt-4">
					<Alert
						variant="warning"
						title="Statement type mismatch"
						dismissible
						onDismiss={typeMismatch.onDismiss}
					>
						<p>
							This looks like{' '}
							{/^[aeiou]/i.test(
								formatRatePlanType(typeMismatch.detectedRatePlanType),
							)
								? 'an'
								: 'a'}{' '}
							<span className="font-semibold">
								{formatRatePlanType(typeMismatch.detectedRatePlanType)}
							</span>{' '}
							statement, but it was analyzed as{' '}
							<span className="font-semibold">
								{formatRatePlanType(typeMismatch.selectedRatePlanType)}
							</span>
							. Do you want to process it as{' '}
							{formatRatePlanType(typeMismatch.detectedRatePlanType)}?
						</p>
						<div className="mt-3 flex items-center gap-3">
							<Button
								size="sm"
								variant="warning"
								onClick={typeMismatch.onRecalculateAsDetected}
								disabled={typeMismatch.isRecalculating}
							>
								{typeMismatch.isRecalculating
									? 'Recalculating…'
									: `Process as ${formatRatePlanType(typeMismatch.detectedRatePlanType)}`}
							</Button>
							<Button
								size="sm"
								variant="ghost"
								onClick={typeMismatch.onDismiss}
								disabled={typeMismatch.isRecalculating}
							>
								Keep my selection
							</Button>
						</div>
					</Alert>
				</div>
			)}

			{/* Customize Fee Structure Modal */}
			{isCustomizeModalOpen && customRates && (
				<CustomizeFeeStructureModal
					isOpen={isCustomizeModalOpen}
					onClose={() => onCustomizeModalOpenChange(false)}
					onConfirm={handleConfirmFeeStructure}
					fileId={document.id}
					context="analysis"
					customRatesData={customRates}
				/>
			)}
		</div>
	);
};

export default AnalysisHeader;
